import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { INDIAN_STATES, sanitizeGstin } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { Plus, PencilSimple, Trash, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { kind: "customer", name: "", gstin: "", contact_person: "", phone: "", email: "", billing_address: "", shipping_address: "", city: "", state: "", state_code: "", pincode: "" };

export default function PartiesPage({ kind }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, kind });
  const [loading, setLoading] = useState(false);
  const label = kind === "customer" ? "Customer" : "Vendor";
  const tableLabel = kind === "customer" ? "Customers" : "Vendors";

  const load = async () => {
    const { data } = await api.get("/parties", { params: { kind, q: q || undefined } });
    setRows(data);
  };

  useEffect(() => { load(); }, [kind]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const onStateChange = (code) => {
    const st = INDIAN_STATES.find(s => s.code === code);
    setForm(f => ({ ...f, state_code: code, state: st?.name || "" }));
  };

  const openCreate = () => { setForm({ ...EMPTY, kind }); setEditing("new"); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r.id); };
  const close = () => { setEditing(null); };

  const save = async () => {
    setLoading(true);
    try {
      if (editing === "new") {
        await api.post("/parties", form);
        toast.success(`${label} created`);
      } else {
        await api.put(`/parties/${editing}`, form);
        toast.success(`${label} updated`);
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}?`)) return;
    try {
      await api.delete(`/parties/${id}`);
      toast.success(`${label} deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`parties-page-${kind}`}>
      <PageHeader
        title={tableLabel}
        subtitle={`Manage your ${tableLabel.toLowerCase()} master with GSTIN and addresses.`}
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                data-testid={`search-${kind}`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder={`Search name, GSTIN, phone, city…`}
                className="pl-8 pr-3 py-2 text-sm bg-white border border-input rounded-md focus:ring-2 focus:ring-brand-secondary outline-none w-56"
              />
            </div>
            <button onClick={openCreate} className="btn-primary flex items-center gap-1.5" data-testid={`add-${kind}-button`}>
              <Plus size={14} weight="bold" /> New {label}
            </button>
          </>
        }
      />

      <div className="p-8">
        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr>
              <th>Name</th><th>GSTIN</th><th>Contact</th><th>City / State</th><th>Phone</th><th className="text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-ink-muted">No {tableLabel.toLowerCase()} yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} data-testid={`party-row-${r.id}`}>
                  <td className="font-semibold text-ink">{r.name}</td>
                  <td className="font-mono text-xs">{r.gstin || "—"}</td>
                  <td>{r.contact_person || "—"}</td>
                  <td>{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                  <td>{r.phone || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(r)} className="btn-ghost inline-flex items-center gap-1" data-testid={`edit-party-${r.id}`}><PencilSimple size={14} /> Edit</button>
                    <button onClick={() => remove(r.id)} className="btn-ghost inline-flex items-center gap-1 text-destructive hover:text-destructive" data-testid={`delete-party-${r.id}`}><Trash size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={close} data-testid="party-modal">
          <div className="bg-white rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="font-display text-lg font-semibold">{editing === "new" ? `New ${label}` : `Edit ${label}`}</div>
              <button onClick={close} className="btn-ghost" data-testid="close-party-modal"><X size={16} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input data-testid="party-name" className="field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="GSTIN (15 char)">
                <div className="flex gap-1.5">
                  <input data-testid="party-gstin-input" className="field font-mono uppercase flex-1" value={form.gstin} onChange={e => setForm({ ...form, gstin: sanitizeGstin(e.target.value) })} placeholder="15-char GSTIN" />
                  <button type="button" onClick={async () => {
                    const g = (form.gstin || "").trim().toUpperCase();
                    if (g.length !== 15) { toast.error("Enter a valid 15-char GSTIN first"); return; }
                    try {
                      const { data } = await api.get(`/gstin/lookup/${g}`);
                      setForm(f => ({
                        ...f,
                        name: data.legal_name || data.trade_name || f.name,
                        billing_address: data.address_line || f.billing_address,
                        city: data.city || f.city,
                        state: data.state || f.state,
                        state_code: data.state_code || f.state_code,
                        pincode: data.pincode || f.pincode,
                      }));
                      toast.success(`Auto-filled: ${data.legal_name}`);
                    } catch (e) {
                      toast.error(formatApiError(e?.response?.data?.detail) || "GSTIN lookup failed");
                    }
                  }} className="btn-secondary !px-2.5 text-xs whitespace-nowrap" data-testid="party-gstin-lookup">Look up</button>
                </div>
              </Field>
              <Field label="Contact Person">
                <input data-testid="party-contact" className="field" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input data-testid="party-phone" className="field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Email">
                <input data-testid="party-email" className="field" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="State">
                <select data-testid="party-state" className="field" value={form.state_code} onChange={e => onStateChange(e.target.value)}>
                  <option value="">Select state</option>
                  {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
                </select>
              </Field>
              <Field label="City">
                <input data-testid="party-city" className="field" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
              </Field>
              <Field label="Pincode">
                <input data-testid="party-pincode" className="field" value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} />
              </Field>
              <Field label="Billing Address" full>
                <textarea rows={2} data-testid="party-billing-address" className="field" value={form.billing_address} onChange={e => setForm({ ...form, billing_address: e.target.value })} />
              </Field>
              <Field label="Shipping Address" full>
                <textarea rows={2} data-testid="party-shipping-address" className="field" value={form.shipping_address} onChange={e => setForm({ ...form, shipping_address: e.target.value })} />
              </Field>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted">
              <button onClick={close} className="btn-secondary" data-testid="cancel-party">Cancel</button>
              <button onClick={save} disabled={loading || !form.name} className="btn-primary" data-testid="save-party">{loading ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      <FieldStyle />
    </div>
  );
}

function Field({ label, children, full, required }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="label-overline block mb-1.5">{label}{required ? " *" : ""}</div>
      {children}
    </div>
  );
}

// .field styles now live globally in index.css; kept for backwards-compatible imports.
export function FieldStyle() { return null; }
